import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomsCalculation, SearchCustomsCalculationsRequest } from '../../../../services/api/customs-calculations';
import { PageResponse } from '../../../../services/api/page-response';
import { LanguageService } from '../../../../services/i18n/language';
import { PaginationComponent } from '../../pagination/pagination.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-calculations-list',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, PaginationComponent],
  templateUrl: './calculations-list.html',
  styleUrl: './calculations-list.scss'
})
export class CalculationsListComponent implements OnInit, OnDestroy {
  @Input() calculations: CustomsCalculation[] = [];
  @Input() calculationsPage: PageResponse<CustomsCalculation> | null = null;
  @Input() loading = false;
  @Input() error: string | null = null;
  @Input() showCreateButton = true;
  @Input() showDeleteButton = true;
  @Input() showOwnerColumn = false;
  @Input() backRoute: string = '/';

  @Output() createCalculation = new EventEmitter<void>();
  @Output() viewCalculation = new EventEmitter<number>();
  @Output() deleteCalculation = new EventEmitter<number>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() filterChange = new EventEmitter<SearchCustomsCalculationsRequest>();

  // Filters
  dateFrom: string | null = null;
  dateTo: string | null = null;
  title: string = '';

  // Translations
  createCalculationText = 'Создать расчет';
  myCalculationsText = 'Мои расчеты таможенных платежей';
  noCalculationsText = 'У вас пока нет расчетов';
  loadingText = 'Загрузка...';
  calculationTitleText = 'Название';
  productsCountText = 'Товаров';
  createdAtText = 'Дата создания';
  completedWithAiText = 'Выполнено с помощью AI';
  viewText = 'Просмотр';
  deleteText = 'Удалить';
  dateFromText = 'Дата создания от';
  dateToText = 'Дата создания до';
  titleFilterText = 'Название';
  clearFiltersText = 'Очистить фильтры';
  applyFiltersText = 'Применить фильтры';
  ownerColumnText = 'Автор';
  totalAmountText = 'Общая сумма';
  statusText = 'Статус';
  actionsText = 'Действия';
  calculationFallbackPrefixText = 'Расчет #';

  private subscriptions = new Subscription();

  constructor(
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.updateTranslations();

    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getProductsCount(calculation: CustomsCalculation): number {
    return calculation.products?.length || 0;
  }

  getTotalAmount(calculation: CustomsCalculation): number {
    if (!calculation.products || calculation.products.length === 0) {
      return 0;
    }
    return calculation.products.reduce((sum, product) => {
      return sum + (product.totalCustomsPayments || 0);
    }, 0);
  }

  onPageChange(page: number): void {
    this.pageChange.emit(page);
  }

  applyFilters(): void {
    const request: SearchCustomsCalculationsRequest = {
      dateFrom: this.dateFrom || undefined,
      dateTo: this.dateTo || undefined,
      title: this.title?.trim() || undefined
    };
    this.filterChange.emit(request);
  }

  clearFilters(): void {
    this.dateFrom = null;
    this.dateTo = null;
    this.title = '';
    this.applyFilters();
  }

  hasActiveFilters(): boolean {
    return !!(this.dateFrom || this.dateTo || (this.title && this.title.trim()));
  }

  onView(id: number): void {
    this.viewCalculation.emit(id);
  }

  statusLabel(status?: string): string {
    switch (status) {
      case 'IN_PROCESSING': return 'В обработке';
      case 'PROCESSED': return 'Обработан';
      case 'CREATED':
      default: return 'Создан';
    }
  }

  onDelete(id: number, event: Event): void {
    event.stopPropagation();
    this.deleteCalculation.emit(id);
  }

  onCreate(): void {
    this.createCalculation.emit();
  }

  private updateTranslations(): void {
    this.createCalculationText = this.languageService.translate('public.dashboard.createCalculation') || this.createCalculationText;
    this.myCalculationsText = this.languageService.translate('public.dashboard.myCalculations') || this.myCalculationsText;
    this.noCalculationsText = this.languageService.translate('public.dashboard.noCalculations') || this.noCalculationsText;
    this.loadingText = this.languageService.translate('common.loading') || this.loadingText;
    this.calculationTitleText = this.languageService.translate('public.dashboard.calculationTitle') || this.calculationTitleText;
    this.productsCountText = this.languageService.translate('public.dashboard.productsCount') || this.productsCountText;
    this.createdAtText = this.languageService.translate('common.createdAt') || this.createdAtText;
    this.completedWithAiText = this.languageService.translate('public.dashboard.completedWithAi') || this.completedWithAiText;
    this.viewText = this.languageService.translate('common.view') || this.viewText;
    this.deleteText = this.languageService.translate('common.delete') || this.deleteText;
    this.dateFromText = this.languageService.translate('public.dashboard.dateFrom') || this.dateFromText;
    this.dateToText = this.languageService.translate('public.dashboard.dateTo') || this.dateToText;
    this.titleFilterText = this.languageService.translate('public.dashboard.titleFilter') || this.titleFilterText;
    this.clearFiltersText = this.languageService.translate('public.dashboard.clearFilters') || this.clearFiltersText;
    this.applyFiltersText = this.languageService.translate('public.dashboard.applyFilters') || this.applyFiltersText;
    this.ownerColumnText = this.languageService.translate('public.dashboard.calculationOwner') || this.languageService.translate('customsBroker.dashboard.calculationOwner') || this.ownerColumnText;
    this.totalAmountText = this.languageService.translate('public.dashboard.totalAmount') || this.totalAmountText;
    this.statusText = this.languageService.translate('common.status') || this.statusText;
    this.actionsText = this.languageService.translate('public.viewCalculation.actions') || this.actionsText;
    this.calculationFallbackPrefixText = this.languageService.translate('public.viewCalculation.calculationFallbackPrefix') || this.calculationFallbackPrefixText;
  }
}
